import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Download, FileSignature, Search, Car, X, Loader2,
} from "lucide-react";
import api, { formatCurrency } from "@/lib/api";
import { openPdfBlob } from "@/lib/openPdf";
import { useI18n } from "@/lib/i18n.jsx";

/**
 * Payment Authorization Form generator.
 *
 * Two flows on the same page:
 *   1. Pick a sold vehicle from a list → all data + installment plan auto-fills
 *      from the linked receivable. Click "Generate PDF" and you're done.
 *   2. Generate a blank/manual form by typing customer + installment rows.
 *
 * The PDF endpoint is `POST /api/payment-authorization.pdf` (returns blob).
 */
const LANG_OPTIONS = [
  { value: "pt", label: "Português" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
];

const empty = () => ({ number: "", due_date: "", amount: "", status: "pending" });

export default function PaymentAuthorizationPage({ t }) {
  const { lang: appLang } = useI18n();
  const [lang, setLang] = useState(appLang === "en" ? "en" : appLang === "es" ? "es" : "pt");
  const [mode, setMode] = useState("auto"); // 'auto' | 'manual'

  // === Auto-mode state ===
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedVid, setSelectedVid] = useState("");

  // === Manual-mode state ===
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "", address: "" });
  const [vehicle, setVehicle] = useState({ year: "", make: "", model: "", vin: "", color: "" });
  const [installments, setInstallments] = useState([empty()]);
  const [notes, setNotes] = useState("");

  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/vehicles");
        // Only sold cars are eligible — those are the ones with a buyer + maybe receivable
        const sold = (r.data || []).filter((v) => v.status === "sold");
        setVehicles(sold);
      } catch {
        // silent — fall back to manual mode
      } finally {
        setLoadingVehicles(false);
      }
    })();
  }, []);

  // Auto-mode search filter
  const filteredVehicles = useMemo(() => {
    if (!search.trim()) return vehicles;
    const q = search.toLowerCase();
    return vehicles.filter((v) =>
      `${v.year} ${v.make} ${v.model} ${v.vin || ""} ${v.buyer_name || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [vehicles, search]);

  // Total used in summary chip + sent to backend in manual mode
  const totalAmount = useMemo(() => {
    return installments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  }, [installments]);

  const updateInst = (i, key, value) => {
    setInstallments((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };
  const addInst = () => setInstallments((rows) => [...rows, { ...empty(), number: rows.length + 1 }]);
  const removeInst = (i) => setInstallments((rows) => rows.filter((_, idx) => idx !== i));

  const generatePdf = async () => {
    setGenerating(true);
    try {
      const payload = { lang, notes };
      if (mode === "auto") {
        if (!selectedVid) {
          toast.error("Selecione um carro vendido");
          setGenerating(false);
          return;
        }
        payload.vehicle_id = selectedVid;
      } else {
        // Manual — basic validation
        if (!customer.name?.trim()) {
          toast.error("Informe o nome do cliente");
          setGenerating(false);
          return;
        }
        payload.customer = customer;
        payload.vehicle = vehicle;
        payload.installments = installments
          .filter((r) => r.amount || r.due_date)
          .map((r, i) => ({
            number: Number(r.number || i + 1),
            due_date: r.due_date,
            amount: Number(r.amount || 0),
            status: r.status || "pending",
          }));
        payload.total_amount = totalAmount;
      }
      const res = await api.post("/payment-authorization.pdf", payload, {
        responseType: "blob",
      });
      openPdfBlob(res.data, "payment-authorization.pdf");
      toast.success("Authorization Form gerado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar o PDF");
    } finally {
      setGenerating(false);
    }
  };

  const selectedVehicle = vehicles.find((v) => v.id === selectedVid);

  return (
    <div data-testid="payment-auth-page" className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <p className="label-eyebrow text-primary mb-2 inline-flex items-center gap-1">
            <FileSignature size={11} /> Documento legal
          </p>
          <h1 className="font-display font-black text-3xl uppercase tracking-tighter">
            Payment Authorization Form
          </h1>
          <p className="text-text-secondary text-sm mt-1 max-w-2xl">
            Gere um documento profissional autorizando o cliente a parcelar parte da entrada.
            Disponível em <strong>PT / EN / ES</strong> com campos editáveis no próprio PDF.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-widest text-text-secondary">Idioma</label>
          <select
            data-testid="pa-lang"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="bg-surface border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
          >
            {LANG_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex border border-border" data-testid="pa-mode-toggle">
        {[
          { v: "auto", l: "Atalho da venda", h: "Auto-preenche o cliente + parcelas a partir de um carro vendido" },
          { v: "manual", l: "Manual", h: "Eu preencho cada campo do zero" },
        ].map((opt) => {
          const active = mode === opt.v;
          return (
            <button
              key={opt.v}
              data-testid={`pa-mode-${opt.v}`}
              onClick={() => setMode(opt.v)}
              className={`flex-1 px-4 py-3 text-left transition-colors ${
                active ? "bg-primary text-white" : "bg-surface hover:bg-surface-hover"
              }`}
            >
              <p className={`font-display font-black uppercase text-sm tracking-tight ${active ? "" : "text-text-primary"}`}>
                {opt.l}
              </p>
              <p className={`text-[11px] mt-1 leading-snug ${active ? "text-white/80" : "text-text-secondary"}`}>
                {opt.h}
              </p>
            </button>
          );
        })}
      </div>

      {/* === AUTO MODE === */}
      {mode === "auto" && (
        <div className="bg-surface border border-border p-5 space-y-4">
          <p className="label-eyebrow">Carros vendidos</p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              data-testid="pa-vehicle-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por ano/marca/modelo/comprador/VIN"
              className="w-full bg-background border border-border focus:border-primary focus:outline-none pl-9 pr-3 h-10 text-sm"
            />
          </div>

          {loadingVehicles ? (
            <p className="text-text-secondary text-xs text-center py-6">…</p>
          ) : filteredVehicles.length === 0 ? (
            <div className="border border-border p-6 text-center text-text-secondary text-sm">
              {vehicles.length === 0
                ? "Nenhum carro vendido ainda. Use o modo Manual."
                : "Nenhum carro encontrado nessa busca."}
            </div>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto">
              {filteredVehicles.map((v) => {
                const isSel = selectedVid === v.id;
                const vinTail = v.vin ? String(v.vin).slice(-6).toUpperCase() : "";
                return (
                  <li key={v.id}>
                    <button
                      data-testid={`pa-veh-${v.id}`}
                      onClick={() => setSelectedVid(v.id)}
                      className={`w-full text-left flex items-stretch gap-3 p-3 border transition-colors ${
                        isSel
                          ? "border-primary bg-primary/[0.06]"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {(v.images && v.images[0]) ? (
                        <img src={v.images[0]} alt="" className="w-16 h-14 object-cover shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-16 h-14 grid place-items-center bg-background border border-border text-text-secondary shrink-0">
                          <Car size={18} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm truncate">{v.year} {v.make} {v.model}</p>
                        <p className="text-[10px] text-text-secondary truncate">
                          {vinTail && <span className="font-mono">{vinTail}</span>}
                          {v.buyer_name && <> · 👤 {v.buyer_name}</>}
                        </p>
                        <p className="text-[10px] mt-0.5 text-primary font-display font-bold">
                          {formatCurrency(v.sold_price || v.sale_price || 0)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {selectedVehicle && (
            <div className="bg-background border border-border p-3 text-xs text-text-secondary flex items-start gap-2">
              <FileSignature size={14} className="text-primary mt-0.5 shrink-0" />
              <div>
                Vamos gerar o PDF com <strong className="text-text-primary">{selectedVehicle.buyer_name || "—"}</strong>,
                veículo <strong className="text-text-primary">{selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}</strong>.
                Se houver um recebível ligado a este carro, as parcelas serão importadas automaticamente.
              </div>
            </div>
          )}
        </div>
      )}

      {/* === MANUAL MODE === */}
      {mode === "manual" && (
        <>
          {/* Customer */}
          <div className="bg-surface border border-border p-5 space-y-3" data-testid="pa-customer-block">
            <p className="label-eyebrow">Cliente</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField label="Nome completo" value={customer.name} onChange={(v) => setCustomer((c) => ({ ...c, name: v }))} testid="pa-cust-name" required />
              <TextField label="Telefone" value={customer.phone} onChange={(v) => setCustomer((c) => ({ ...c, phone: v }))} testid="pa-cust-phone" />
              <TextField label="Email" value={customer.email} onChange={(v) => setCustomer((c) => ({ ...c, email: v }))} testid="pa-cust-email" />
              <TextField label="Endereço" value={customer.address} onChange={(v) => setCustomer((c) => ({ ...c, address: v }))} testid="pa-cust-address" />
            </div>
          </div>

          {/* Vehicle */}
          <div className="bg-surface border border-border p-5 space-y-3">
            <p className="label-eyebrow">Veículo</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <TextField label="Ano" value={vehicle.year} onChange={(v) => setVehicle((c) => ({ ...c, year: v }))} testid="pa-veh-year" />
              <TextField label="Marca" value={vehicle.make} onChange={(v) => setVehicle((c) => ({ ...c, make: v }))} testid="pa-veh-make" />
              <TextField label="Modelo" value={vehicle.model} onChange={(v) => setVehicle((c) => ({ ...c, model: v }))} testid="pa-veh-model" />
              <TextField label="Cor" value={vehicle.color} onChange={(v) => setVehicle((c) => ({ ...c, color: v }))} testid="pa-veh-color" />
              <TextField label="VIN" value={vehicle.vin} onChange={(v) => setVehicle((c) => ({ ...c, vin: v }))} testid="pa-veh-vin" />
            </div>
          </div>

          {/* Installments */}
          <div className="bg-surface border border-border p-5 space-y-3">
            <div className="flex items-baseline justify-between">
              <p className="label-eyebrow">Parcelas</p>
              <span className="text-xs text-text-secondary">
                Total · <strong className="text-primary font-display font-bold">{formatCurrency(totalAmount)}</strong>
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-[10px] uppercase tracking-widest border-b border-border">
                  <th className="text-left py-2 w-16">#</th>
                  <th className="text-left py-2">Vencimento</th>
                  <th className="text-right py-2">Valor (US$)</th>
                  <th className="py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {installments.map((row, i) => (
                  <tr key={i} className="border-b border-border last:border-b-0">
                    <td className="py-2">
                      <input
                        data-testid={`pa-inst-num-${i}`}
                        value={row.number || i + 1}
                        onChange={(e) => updateInst(i, "number", e.target.value)}
                        className="w-12 bg-background border border-border px-2 h-8 text-center"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="date"
                        data-testid={`pa-inst-date-${i}`}
                        value={row.due_date}
                        onChange={(e) => updateInst(i, "due_date", e.target.value)}
                        className="bg-background border border-border px-2 h-8 text-sm"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <input
                        type="number"
                        step="0.01"
                        data-testid={`pa-inst-amount-${i}`}
                        value={row.amount}
                        onChange={(e) => updateInst(i, "amount", e.target.value)}
                        className="w-32 bg-background border border-border px-2 h-8 text-right font-mono"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        data-testid={`pa-inst-remove-${i}`}
                        onClick={() => removeInst(i)}
                        disabled={installments.length === 1}
                        className="text-text-secondary hover:text-primary disabled:opacity-30 transition-colors p-1"
                        title="Remover linha"
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              data-testid="pa-inst-add"
              onClick={addInst}
              className="text-[11px] uppercase tracking-widest text-primary hover:text-primary-hover inline-flex items-center gap-1 font-display font-bold"
            >
              <Plus size={12} /> Adicionar parcela
            </button>
          </div>

          {/* Notes */}
          <div className="bg-surface border border-border p-5 space-y-2">
            <p className="label-eyebrow">Observações</p>
            <textarea
              data-testid="pa-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Ex: Acordo verbal com o cliente em 22/05, parcela inicial sem juros..."
              className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm resize-y"
            />
          </div>
        </>
      )}

      {/* Generate button */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border -mx-4 px-4 py-4 flex items-center justify-end gap-3">
        <button
          type="button"
          data-testid="pa-generate"
          onClick={generatePdf}
          disabled={generating || (mode === "auto" && !selectedVid)}
          className="bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 h-12 inline-flex items-center gap-2 font-display font-bold uppercase tracking-widest text-sm"
        >
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {generating ? "Gerando..." : "Gerar PDF"}
        </button>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testid, required }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}{required && <span className="text-primary"> *</span>}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-background border border-border focus:border-primary focus:outline-none px-3 h-10 text-sm"
      />
    </div>
  );
}
